/* eslint-disable react-refresh/only-export-components */
import { createFileRoute } from '@tanstack/react-router';
import { createColumnHelper } from '@tanstack/react-table';
import { Archive, ArchiveRestore, Plus } from 'lucide-react';
import { useState } from 'react';
import { z } from 'zod';
import type { ICapFinding } from '@/api/findings/types';
import { DataTable } from '@/components/shared/DataTable';
import { TagListValue, TextValue } from '@/components/shared/ValueRenderers';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { useTranslator } from '@/hooks/useTranslator';
import { FINDING_SOURCE_TYPES } from '@/lib/constants/capa';
import { useSessionStore } from '@/store/sessionStore';
import { CreateFindingDialog } from './-components/CreateFindingDialog';
import { CreatePlanDialog } from './-components/CreatePlanDialog';
import { FindingDetailsSheet } from './-components/FindingDetailsSheet';
import { FindingStatusBadge } from './-components/FindingStatusBadge';
import { UrgencyCell } from './-components/UrgencyCell';
import { useArchiveFindingsMutation } from './-hooks/useArchiveFindingsMutation';
import { useFindingsQuery } from './-hooks/useFindingsQuery';
import { useUnarchiveFindingsMutation } from './-hooks/useUnarchiveFindingsMutation';

const searchSchema = z.object({
  page: z.number().int().min(1).optional(),
  sort: z.string().optional(),
});

export const Route = createFileRoute(
  '/(client)/network/(pages)/(detail)/$companyId/audits/$auditId/findings/',
)({
  validateSearch: searchSchema,
  component: FindingsPage,
});

const colHelper = createColumnHelper<ICapFinding>();

function FindingsPage() {
  const { companyId, auditId } = Route.useParams();
  const search = Route.useSearch();
  const { getTranslation: t } = useTranslator({ page: 'findings' });
  const user = useSessionStore(s => s.user);

  const isNetworkAudit = companyId !== user?.companyId;

  const [activeView, setActiveView] = useState<'active' | 'archived'>('active');
  const [selectedFindingId, setSelectedFindingId] = useState<string | null>(
    null,
  );
  const [createFindingOpen, setCreateFindingOpen] = useState(false);
  const [createPlanOpen, setCreatePlanOpen] = useState(false);
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});

  const page = search.page ?? 1;
  const sort = search.sort;
  const isArchiveView = activeView === 'archived';

  const { data, isLoading } = useFindingsQuery({
    auditId,
    isNetworkAudit,
    page,
    count: 20,
    ...(sort !== undefined ? { sort } : {}),
    filter: { isArchived: isArchiveView },
    isArchiveView,
  });

  const rows = data?.data ?? [];
  const total = data?.meta?.totalCount ?? 0;

  const archiveMutation = useArchiveFindingsMutation(auditId);
  const unarchiveMutation = useUnarchiveFindingsMutation(auditId);

  const selectedIds = Object.entries(rowSelection)
    .filter(([, selected]) => selected)
    .map(([id]) => id);

  function clearSelection() {
    setRowSelection({});
  }

  async function handleArchive() {
    try {
      await archiveMutation.mutateAsync({ findingIds: selectedIds });
      clearSelection();
    } catch {
      // mutation hook invalidates cache; no extra handling needed
    }
  }

  async function handleUnarchive() {
    try {
      await unarchiveMutation.mutateAsync({ findingIds: selectedIds });
      clearSelection();
    } catch {
      // mutation hook invalidates cache; no extra handling needed
    }
  }

  const columns = [
    colHelper.display({
      id: 'rowNumber',
      header: () => null,
      cell: ({ row }) => (
        <span className='text-xs text-muted-foreground'>
          {row.index + 1 + (page - 1) * 20}
        </span>
      ),
      size: 40,
    }),
    colHelper.accessor('title', {
      header: t('columns.title'),
      cell: ({ getValue, row }) => (
        <button
          type='button'
          className='text-xs font-medium text-primary hover:underline text-left truncate max-w-full'
          onClick={() => setSelectedFindingId(row.original.id)}
        >
          {getValue() || '—'}
        </button>
      ),
    }),
    colHelper.accessor('type', {
      header: t('columns.sourceType'),
      cell: ({ getValue }) => (
        <TextValue
          value={FINDING_SOURCE_TYPES[getValue()] ?? getValue()}
          emptyNode={<span className='text-xs text-muted-foreground'>—</span>}
        />
      ),
    }),
    colHelper.accessor('status', {
      header: t('columns.status'),
      cell: ({ getValue }) => <FindingStatusBadge status={getValue()} />,
    }),
    colHelper.accessor('urgency', {
      header: t('columns.urgency'),
      cell: ({ getValue }) => <UrgencyCell urgency={getValue()} />,
    }),
    colHelper.accessor('tags', {
      header: t('columns.tags'),
      cell: ({ getValue }) => (
        <TagListValue
          labels={(getValue() ?? []).map(tag => tag.title)}
          emptyNode={<span className='text-xs text-muted-foreground'>—</span>}
        />
      ),
    }),
    colHelper.accessor('actionCount', {
      header: t('columns.actions'),
      cell: ({ getValue }) => (
        <span className='text-xs'>{getValue() ?? 0}</span>
      ),
    }),
    colHelper.accessor('planCount', {
      header: t('columns.plans'),
      cell: ({ getValue }) => (
        <span className='text-xs'>{getValue() ?? 0}</span>
      ),
    }),
  ];

  return (
    <div className='flex flex-col flex-1 min-h-0 overflow-hidden'>
      <DataTable
        storageKey={`audit-findings-${auditId}`}
        data={rows}
        columns={columns}
        loading={isLoading}
        rowIdKey='id'
        rowSelection={rowSelection}
        onRowSelectionChange={setRowSelection}
        pagination={{ pageIndex: page - 1, pageSize: 20 }}
        manualPageCount={Math.ceil(total / 20)}
        manualRowCount={total}
        emptyMessage={t('table.empty.title')}
        toolbarActions={[
          {
            id: 'view-toggle',
            type: 'custom' as const,
            content: (
              <ToggleGroup
                value={[activeView]}
                onValueChange={v => {
                  const next = v[v.length - 1] as
                    | 'active'
                    | 'archived'
                    | undefined;
                  if (next) {
                    setActiveView(next);
                    clearSelection();
                  }
                }}
                className='self-start w-fit'
              >
                <ToggleGroupItem
                  value='active'
                  className='inline-flex items-center gap-1.5 h-auto py-1.5 px-3 text-xs'
                >
                  {t('toolbar.active')}
                </ToggleGroupItem>
                <ToggleGroupItem
                  value='archived'
                  className='inline-flex items-center gap-1.5 h-auto py-1.5 px-3 text-xs'
                >
                  {t('toolbar.archived')}
                </ToggleGroupItem>
              </ToggleGroup>
            ),
          },
          {
            id: 'create-plan',
            type: 'button' as const,
            label: t('toolbar.createPlan'),
            icon: <Plus className='size-3.5' />,
            onClick: () => setCreatePlanOpen(true),
          },
          {
            id: 'add-finding',
            type: 'button' as const,
            label: t('toolbar.addFinding'),
            icon: <Plus className='size-3.5' />,
            onClick: () => setCreateFindingOpen(true),
          },
        ]}
        bulkActions={
          isArchiveView
            ? [
                {
                  id: 'unarchive',
                  type: 'button' as const,
                  label: t('bulk.unarchive'),
                  icon: <ArchiveRestore className='size-3.5' />,
                  onClick: handleUnarchive,
                  loading: unarchiveMutation.isPending,
                },
              ]
            : [
                {
                  id: 'archive',
                  type: 'button' as const,
                  label: t('bulk.archive'),
                  icon: <Archive className='size-3.5' />,
                  onClick: handleArchive,
                  loading: archiveMutation.isPending,
                },
              ]
        }
      />

      <FindingDetailsSheet
        findingId={selectedFindingId}
        onClose={() => setSelectedFindingId(null)}
        companyId={companyId}
      />

      <CreateFindingDialog
        open={createFindingOpen}
        onClose={() => setCreateFindingOpen(false)}
        auditId={auditId}
        companyId={companyId}
      />

      {selectedFindingId && (
        <CreatePlanDialog
          open={createPlanOpen}
          onClose={() => setCreatePlanOpen(false)}
          findingId={selectedFindingId}
          companyId={companyId}
          auditId={auditId}
        />
      )}
    </div>
  );
}
